import React from 'react';
import {
  Address,
  AddressStateType,
  AddressStatusType,
  AutoCompleteSuggestionType,
} from 'core-lib/types/Address';
import { debounce } from 'core-lib/utils/helpers';
import { getAutoCompleteSuggestions } from 'core-lib/services/getAutoCompleteSuggestions';
import { getPlaceDetails, getPlaceDetailsNonAuthenticated } from 'core-lib/services/getPlaceDetils';
import { API_STATUS } from 'core-lib/constants/network';
import { useProfile } from 'core-lib/hooks/useProfile';

function serializeSuggestions(data: any) {
  if (!data) return [];

  return data.map(elem => ({
    primaryText: elem?.structured_formatting?.main_text,
    secondaryText: elem?.structured_formatting?.secondary_text,
    placeId: elem?.place_id,
  }));
}

const getAddressComponent = (data: any[], addressString: string) => {
  let isAddressComponentFound = false;
  let addressComponent = '';
  for (let i = data.length - 1; i >= 0; i--) {
    if (isAddressComponentFound) break;
    const { types, long_name: name } = data[i];
    for (let j = 0; j < types.length; j++) {
      if (types[j] === addressString) {
        addressComponent = name;
        isAddressComponentFound = true;
        break;
      }
    }
  }

  return addressComponent;
};

function extractAddressForNonAuthenticated(result: any): Address {
  let pincode = result?.pincode || null;
  let city = result?.city || null;
  let state = result?.state || null;
  let formattedAddress = result?.formatted_address || null;
  let lat = result?.lat || null;
  let lng = result?.lng || null;

  return {
    pincode,
    city,
    state,
    locality: formattedAddress,
    latitude: lat,
    longitude: lng,
    country: 'India',
  };
}

function extractAddress(result: any): Address {
  const loc = result?.geometry?.location;

  let pincode = null;
  let city = null;
  let state = null;
  let formattedAddress = null;
  let lat = null;
  let lng = null;

  const addPinCode = getAddressComponent(result.address_components, 'postal_code');
  const addCity = getAddressComponent(result.address_components, 'locality') ||
    getAddressComponent(result.address_components, 'administrative_area_level_2');
  const addState = getAddressComponent(result.address_components, 'administrative_area_level_1');
  if (addPinCode) pincode = addPinCode;
  if (addCity) city = addCity;
  if (addState) state = addState;
  if (loc.lat) lat = loc.lat;
  if (loc.lng) lng = loc.lng;
  if (result.formatted_address) formattedAddress = result.formatted_address;

  return {
    pincode,
    city,
    state,
    locality: formattedAddress,
    latitude: lat,
    longitude: lng,
    country: 'India',
    name: result?.name || null,
    vicinity: result?.vicinity || null,
  };
}

export type AutoCompleteResultsTypes = {
  address: AddressStateType;
  suggestions: AutoCompleteSuggestionType[];
  query: string;
  updateQuery: (args: string) => void;
  selectPlaceId: (data: AutoCompleteSuggestionType) => void;
  reset: () => void;
  suggestionAPiStatus: string;
};

export default function useAutoComplete(serviceName?: string): AutoCompleteResultsTypes {
  const { isAuthenticated } = useProfile();
  const [query, setQuery] = React.useState<string>('');
  const [address, setAddress] = React.useState<AddressStateType>({
    status: AddressStatusType.Default,
    data: null,
  });
  const [suggestions, setSuggestions] = React.useState<AutoCompleteSuggestionType[]>([]);
  const [suggestionAPiStatus, setSuggestionsApiStatus] = React.useState(API_STATUS.default);

  function reset() {
    setQuery('');
    setAddress({
      status: AddressStatusType.Default,
      data: null,
    });
    setSuggestions([]);
  }

  async function fetchPlaceDetail(data: AutoCompleteSuggestionType) {
    let result:any;
    setQuery(data.primaryText);

    if (isAuthenticated) {
      result = await getPlaceDetails(data.placeId);
      const loc = result?.geometry?.location;
      if (loc && result.address_components) {
        const address: Address = extractAddress(result);
        if (address.latitude && address.longitude) {
          setAddress({
            status: AddressStatusType.Success,
            data: address,
          });
        } else {
          setAddress({
            status: AddressStatusType.Failed,
            data: null,
          });
        }
      } else {
        setAddress({
          status: AddressStatusType.Failed,
          data: null,
        });
      }
    } else {
      result = await getPlaceDetailsNonAuthenticated(data.placeId, serviceName);
      if (result) {
        const address: Address = extractAddressForNonAuthenticated(result);
        if (address.latitude && address.longitude) {
          setAddress({
            status: AddressStatusType.Success,
            data: address,
          });
        } else {
          setAddress({
            status: AddressStatusType.Failed,
            data: null,
          });
        }
      } else {
        setAddress({
          status: AddressStatusType.Failed,
          data: null,
        });
      }
    }
  }

  React.useEffect(() => {
    if (query?.length) {
      debounce(async () => {
        setSuggestionsApiStatus(API_STATUS.in_progress);
        const result = await getAutoCompleteSuggestions(query, serviceName);
        setSuggestions(serializeSuggestions(result));
        setSuggestionsApiStatus(API_STATUS.success);
      }, 300);
    } else {
      reset();
      setSuggestionsApiStatus(API_STATUS.default);
    }
  }, [query]);

  return {
    address,
    suggestions,
    query,
    updateQuery: (q) => setQuery(q),
    selectPlaceId: fetchPlaceDetail,
    reset,
    suggestionAPiStatus,
  };
}
